import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiAlertTriangle, FiX } from 'react-icons/fi';
import { Textarea } from '@/shared/components/ui/Textarea';

const MIN_REASON_LENGTH = 5;

interface OverrideReasonDialogProps {
  isOpen: boolean;
  /**
   * Contexto define o título/explicação:
   *   - `skip-test`: admin marcou opt-out do teste pré-deploy
   *   - `accept-regression`: admin viu regressão e quer publicar mesmo assim
   */
  context: 'skip-test' | 'accept-regression';
  /** Recebe a justificativa validada (≥5 chars). */
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

const CONTEXT_COPY = {
  'skip-test': {
    title: 'Pular teste pré-deploy',
    description:
      'O teste pré-deploy roda cenários contra o novo prompt antes de publicar — assim a gente sabe se a mudança não regrediu nada importante. Você está optando por pular esse passo. Justifique brevemente por que está publicando direto:',
    placeholder:
      'Ex: hotfix de tom para reduzir mensagens longas — comportamento crítico não foi alterado.',
    confirmLabel: 'Pular teste e salvar',
  },
  'accept-regression': {
    title: 'Aceitar regressão e salvar',
    description:
      'O teste pré-deploy detectou regressão neste prompt. Você está optando por publicar mesmo assim. Justifique brevemente o motivo:',
    placeholder:
      'Ex: a regressão é em métricas secundárias; ganho de transparência compensa.',
    confirmLabel: 'Salvar mesmo assim',
  },
} as const;

export const OverrideReasonDialog: React.FC<OverrideReasonDialogProps> = ({
  isOpen,
  context,
  onConfirm,
  onCancel,
}) => {
  const [reason, setReason] = useState('');
  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON_LENGTH;
  const remaining = Math.max(0, MIN_REASON_LENGTH - trimmed.length);
  const copy = CONTEXT_COPY[context];

  useEffect(() => {
    if (isOpen) setReason('');
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center sm:p-4"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      data-testid="override-reason-dialog"
    >
      <div
        className="relative w-full sm:max-w-lg rounded-t-2xl sm:rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[92vh]"
        style={
          {
            backgroundColor: '#ffffff',
            maxHeight: '90vh',
            '--theme-text': '#1e293b',
            '--theme-muted-text': '#6b7280',
            '--theme-input-background': '#ffffff',
            '--theme-input-text': '#1e293b',
            '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
            '--theme-input-focus': '#8b5cf6',
            '--theme-card-background': '#ffffff',
            '--theme-card-text': '#334155',
            '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
            '--theme-primary': '#8b5cf6',
          } as React.CSSProperties
        }
      >
        <div
          className="flex items-center justify-between px-5 py-4 border-b"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <div className="flex items-center gap-2">
            <FiAlertTriangle className="h-5 w-5" style={{ color: '#f59e0b' }} />
            <h2 className="text-lg font-semibold text-slate-800">{copy.title}</h2>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1 rounded hover:bg-slate-100 transition-colors"
            style={{ color: '#64748b' }}
            aria-label="Fechar"
            data-testid="override-reason-dialog-close"
          >
            <FiX className="h-5 w-5" />
          </button>
        </div>

        <div className="px-5 py-5 flex-1 space-y-3">
          <p className="text-sm text-slate-600 leading-relaxed">
            {copy.description}
          </p>

          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={copy.placeholder}
            rows={4}
            data-testid="override-reason-dialog-textarea"
          />

          <div className="text-xs" style={{ color: '#94a3b8' }}>
            {isValid ? (
              <span style={{ color: '#10b981' }}>
                Justificativa válida ({trimmed.length} caracteres).
              </span>
            ) : (
              <>Mínimo de {MIN_REASON_LENGTH} caracteres. Faltam {remaining}.</>
            )}
          </div>
        </div>

        <div
          className="flex items-center justify-end gap-3 px-5 py-4 border-t"
          style={{ borderColor: 'rgba(0, 0, 0, 0.06)' }}
        >
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-md text-sm font-medium"
            style={{ color: '#64748b', backgroundColor: 'transparent' }}
            data-testid="override-reason-dialog-cancel"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => onConfirm(trimmed)}
            disabled={!isValid}
            className="px-4 py-2 rounded-md text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              backgroundColor: isValid ? '#8b5cf6' : '#cbd5e1',
              color: '#ffffff',
            }}
            data-testid="override-reason-dialog-confirm"
          >
            {copy.confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
